import { useCallback, useEffect } from 'react';
import type { PageExperience } from '@/features/experiences/types';
import { useQueryParams } from '@/hooks/useQueryParams';
import { getDefaultChild } from '../planner.lib';
import { useLastPlannerView } from './useLastPlannerView';

export const VIEW_QUERY_PARAM = 'view';

// The ?view= URL param is the source of truth for the planner's active child
// view, so specific views are deep-linkable. A missing or invalid param falls
// back to the view the user last landed on in this planner, then to the default
// child — neither fallback rewrites the URL, so a paramless URL stays the
// canonical address of the planner rather than of one view.
//
// The default child is the owner's or editor's choice when they have set one
// (experience.defaultChildExperienceId), derived otherwise. It deliberately
// sits below the remembered view: an author picks where newcomers land, not
// where a returning visitor resumes.
export const useActiveView = (experience: PageExperience) => {
  const { queryParams, setQueryParams } = useQueryParams();
  const { lastViewId, rememberView } = useLastPlannerView(experience.id);

  const viewParam = queryParams.get(VIEW_QUERY_PARAM);
  const paramChildId = experience.children.find(
    (child) => child.id === viewParam
  )?.id;
  const rememberedChildId = experience.children.find(
    (child) => child.id === lastViewId
  )?.id;
  const activeChildId =
    paramChildId ??
    rememberedChildId ??
    getDefaultChild(experience.children, experience.defaultChildExperienceId)
      ?.id;

  // Remember whichever child the param resolves to, so picking a view from any
  // of the pickers, creating one, and following a deep link all persist through
  // one rule — and re-record a restored one so simply reopening a planner keeps
  // it out of the eviction queue.
  //
  // Only an unambiguous visit counts. A URL that names a view we can't honor —
  // the customize flow's `view=add` sentinel, or a deleted child's id — says
  // nothing about which view the user is on, so it neither records nor
  // refreshes recency; that's what keeps those values out of storage, with no
  // sentinel check needed. (A child-scoped mount, whose `children` is empty,
  // likewise records nothing.) The default is excluded too: recording it would
  // pin a planner to whichever view was default the day the visitor first
  // arrived, so a later change by the author would never reach them.
  //
  // Rendering still falls back to the remembered child for those URLs — only
  // recording is withheld. Runs post-commit: writing to an external store
  // during render is unsafe under concurrent rendering.
  const recordedChildId = viewParam === null ? rememberedChildId : paramChildId;
  useEffect(() => {
    if (recordedChildId) {
      rememberView(recordedChildId);
    }
  }, [recordedChildId, rememberView]);

  const setActiveView = useCallback(
    (childId: string) =>
      setQueryParams({ [VIEW_QUERY_PARAM]: childId }, { replace: true }),
    [setQueryParams]
  );

  return { activeChildId, setActiveView } as const;
};
